import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { authService } from '../../features/auth/services/authService';
import PasswordStrengthChecker, { isPasswordStrong } from '../../features/sharedComponents/PasswordStrengthChecker';

const ChangePassword: React.FC = () => {
    const navigate = useNavigate();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const [loading, setLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmNewPassword) {
            Swal.fire({
                icon: 'warning',
                title: 'Passwords do not match',
                text: 'Please ensure both new passwords match.',
                confirmButtonColor: '#00A63E'
            });
            return;
        }

        if (!isPasswordStrong(newPassword)) {
            Swal.fire({
                icon: 'warning',
                title: 'Weak Password',
                text: 'Please choose a stronger password that meets all requirements.',
                confirmButtonColor: '#00A63E'
            });
            return;
        }

        setLoading(true);
        try {
            const response = await authService.changePassword({
                currentPassword,
                newPassword
            });

            if (response.isSuccess) {
                Swal.fire({
                    icon: 'success',
                    title: 'Password Changed Successfully',
                    text: 'Your password has been updated successfully.',
                    confirmButtonColor: '#00A63E'
                }).then(() => {
                    navigate(-1);
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to change password.',
                    confirmButtonColor: '#00A63E'
                });
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.response?.data?.message || 'Failed to change password.',
                confirmButtonColor: '#00A63E'
            });
        } finally {
            setLoading(false);
        }
    };

    const inputClass = "w-full bg-[#F9FAFB] border border-gray-200 rounded-lg py-3 px-4 text-gray-700 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all placeholder:text-gray-400";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

    return (
        <div className="w-full flex justify-center py-4 sm:py-8">
            <div className="w-full max-w-xl">
                {/* Page Header */}
                <div className="mb-8 text-center">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Change Password
                    </h1>
                    <p className="text-gray-500 text-sm sm:text-base">
                        Update your account password to keep your account secure.
                    </p>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* Card Header */}
                    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                            <KeyRound className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <h2 className="font-semibold text-gray-900">Password Settings</h2>
                            <p className="text-xs text-gray-500">Enter your current password and choose a new one</p>
                        </div>
                    </div>

                    {/* Card Body */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-5">
                        {/* Current Password */}
                        <div>
                            <label className={labelClass}>Current Password</label>
                            <div className="relative">
                                <input
                                    type={showCurrentPassword ? "text" : "password"}
                                    placeholder="Enter current password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className={inputClass}
                                    required
                                />
                                <button
                                    type="button"
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {/* New Password */}
                        <div>
                            <label className={labelClass}>New Password</label>
                            <div className="relative">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    placeholder="Enter new password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className={inputClass}
                                    required
                                />
                                <button
                                    type="button"
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            <PasswordStrengthChecker password={newPassword} />
                        </div>

                        {/* Confirm New Password */}
                        <div>
                            <label className={labelClass}>Confirm New Password</label>
                            <div className="relative">
                                <input
                                    type={showConfirmNewPassword ? "text" : "password"}
                                    placeholder="Re-enter new password"
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                    className={inputClass}
                                    required
                                />
                                <button
                                    type="button"
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                >
                                    {showConfirmNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {confirmNewPassword && newPassword !== confirmNewPassword && (
                                <span className="text-xs text-red-500 mt-1 block">New passwords do not match</span>
                            )}
                        </div>



                        {/* Actions */}
                        <div className="flex items-center gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="flex-1 py-3 rounded-lg font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={loading || !isPasswordStrong(newPassword)}
                                className={`flex-1 py-3 rounded-lg font-semibold transition-all text-white
                                    ${loading || !isPasswordStrong(newPassword) 
                                        ? 'bg-green-300 cursor-not-allowed' 
                                        : 'bg-[#00A63E] hover:bg-[#008c34]'}`}
                            >
                                {loading ? 'Updating...' : 'Update Password'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;